/**
 * `<table-seat>`: one player at the rail - their avatar, their face-down Foot
 * and their hand, turned to face the seat's side of the table.
 */

import type { PlayerView } from "@ryzun/haf-saloon-game-engine";
import type { TeamColor } from "../../server/protocol.js";
import { h, setAttribute } from "../dom.js";
import { emit } from "../events.js";
import type { SeatHolder } from "../lobby.js";
import type { SeatOrientation } from "../seating.js";
import { type SeatRole, describeSeat, getHeldCount } from "../table-text.js";
import { BaseElement } from "./base-element.js";
import type { OwnHand } from "./card-hand.js";
import styles from "./table-seat.css";
import "./card-hand.js";
import "./card-pile.js";
import "./player-avatar.js";

export interface SeatModel {
    readonly player: PlayerView;
    readonly orientation: SeatOrientation;
    readonly color: TeamColor;
    readonly role: SeatRole;
    /** Clock hour, purely for the accessible description of the table. */
    readonly clock: number;
    readonly isTurn: boolean;
    readonly isDealer: boolean;
    readonly holder: SeatHolder;
    /** A passing note about who just came or went, or empty for none. */
    readonly callout: string;
}

function describeStatus(seat: SeatModel): string {
    const held = getHeldCount(seat.player);
    const holding = seat.player.inFoot ? `On Foot · ${held}` : `${held} cards`;

    return seat.isTurn ? `Turn · ${holding}` : holding;
}

export class TableSeat extends BaseElement {
    #seat: SeatModel | null = null;

    #own: OwnHand | null = null;

    readonly #section = h("section", { class: "seat" });

    readonly #avatar = document.createElement("player-avatar");

    readonly #foot = document.createElement("playing-card-pile");

    readonly #hand = document.createElement("playing-card-hand");

    constructor() {
        super(styles);

        this.#foot.type = "foot";
        this.#avatar.append(this.#foot);
        this.#section.append(this.#avatar, this.#hand);
        this.root.append(this.#section);

        // The hand's toggles stop at this seat's shadow root, so pass them on.
        this.#hand.addEventListener("card-toggle", (event) => {
            event.stopPropagation();
            emit(this, "card-toggle", event.detail);
        });
    }

    get seat(): SeatModel | null {
        return this.#seat;
    }

    set seat(value: SeatModel | null) {
        this.#seat = value;
        this.invalidate();
    }

    /** Where cards dealt or drawn into this seat's hand land. */
    get handBox(): DOMRect {
        return this.#hand.getBoundingClientRect();
    }

    /** Where this seat's Foot is set down. */
    get footBox(): DOMRect {
        return this.#foot.getBoundingClientRect();
    }

    /** The viewer's own cards; only ever set on the viewer's seat. */
    set own(value: OwnHand | null) {
        this.#own = value;
        this.invalidate();
    }

    protected render(): void {
        const seat = this.#seat;

        if (seat === null) {
            return;
        }

        setAttribute(this, "orientation", seat.orientation);
        setAttribute(this.#section, "aria-label", describeSeat(seat));
        setAttribute(this.#section, "role", "group");

        this.#avatar.name = seat.player.name;
        this.#avatar.color = seat.color;
        this.#avatar.active = seat.isTurn;
        this.#avatar.dealer = seat.isDealer;
        this.#avatar.align = seat.orientation.includes("east") ? "right" : "left";
        this.#avatar.status = describeStatus(seat);
        this.#avatar.holder = seat.holder;
        this.#avatar.callout = seat.callout;

        // Once a player is on their Foot it is in their hand, not on the table.
        this.#foot.hidden = seat.player.inFoot;
        this.#foot.count = seat.player.footCount;

        this.#hand.orientation = seat.orientation;
        this.#hand.count = getHeldCount(seat.player);
        this.#hand.own = this.#own;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "table-seat": TableSeat;
    }
}

customElements.define("table-seat", TableSeat);
